import { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { techniquesById } from "../data/techniques.js";
import { Icon, familyMeta } from "../components/Icons.jsx";
import { useJournal } from "../lib/journal.jsx";
import Seo from "../components/Seo.jsx";

export default function TechniqueDetail() {
  const { id } = useParams();
  const tech = techniquesById[Number(id)];
  const { saved, toggleSave, addEntry } = useJournal();
  const [note, setNote] = useState("");
  const [saved_, setSaved_] = useState(false);

  if (!tech) return <Navigate to="/techniques" replace />;
  const fam = familyMeta[tech.family];
  const isSaved = saved.includes(tech.id);

  function submitNote(e) {
    e.preventDefault();
    if (!note.trim()) return;
    addEntry({ techniqueId: tech.id, note: note.trim() });
    setNote("");
    setSaved_(true);
    setTimeout(() => setSaved_(false), 2500);
  }

  const schema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: tech.title,
    description: tech.modern,
    totalTime: `PT${tech.time}M`,
    step: tech.steps.map((s, i) => ({ "@type": "HowToStep", position: i + 1, text: s })),
  };

  return (
    <>
      <Seo
        title={`${tech.title} · Technique ${tech.id} · Simple Meditation World`}
        description={tech.modern}
        path={`/technique/${tech.id}`}
        schema={schema}
      />
      <section className="section-night" style={{ paddingTop: 60, paddingBottom: 50 }}>
        <div className="container">
          <Link to="/techniques" style={{ color: "var(--gold)", textDecoration: "none", fontFamily: "var(--mono)", fontSize: 12, letterSpacing: "0.18em", textTransform: "uppercase" }}>
            ← All 112 doorways
          </Link>
          <div style={{ marginTop: 28, color: "var(--gold)", fontFamily: "var(--mono)", fontSize: 12, letterSpacing: "0.22em", textTransform: "uppercase" }}>
            Technique {String(tech.id).padStart(3, "0")} · Source verse {tech.sourceVerse} · {fam.label} Doorway
          </div>
          <h1 style={{ color: "var(--ivory)", fontFamily: "var(--serif)", fontWeight: 400, fontSize: "clamp(36px, 5vw, 64px)", lineHeight: 1.05, marginTop: 14 }}>
            {tech.title}
          </h1>
          <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginTop: 22 }}>
            <span className="audio-chip">📖 Read · 2 min</span>
            <span className="audio-chip">◐ Practice · {tech.time} min</span>
            <span className="audio-chip soon">♪ Listen · guided <span className="badge">Soon</span></span>
          </div>
          <div style={{ marginTop: 28, display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              className="btn btn-primary"
              onClick={() => toggleSave(tech.id)}
              aria-pressed={isSaved}
            >
              {isSaved ? "♥ Saved" : "♡ Save This Technique"}
            </button>
            <Link to="/find-my-doorway" className="btn btn-secondary" style={{ background: "transparent", color: "var(--ivory)", border: "1px solid rgba(229,193,106,0.4)" }}>
              ↻ Try Another Doorway
            </Link>
          </div>
        </div>
      </section>

      <section className="section-ivory">
        <div className="container">
          <div className="featured-wrap">
            <div className="featured-left">
              <div className="tech-block">
                <div className="lbl">Ancient Essence</div>
                <p style={{ fontStyle: "italic" }}>"{tech.essence}"</p>
              </div>
              <div className="tech-block modern">
                <div className="lbl">Modern Translation</div>
                <p>{tech.modern}</p>
              </div>
              <div className="tech-block">
                <div className="lbl">Best For</div>
                <div className="tech-tags" style={{ marginTop: 8 }}>
                  {tech.best.map((t) => <span key={t} className="tech-tag">{t}</span>)}
                </div>
              </div>
            </div>
            <div className="featured-right">
              <h4>Try it now · {tech.time} minutes</h4>
              <ol className="steps">
                {tech.steps.map((s, i) => <li key={i}>{s}</li>)}
              </ol>
              <div className="warn"><b>Common mistake →</b> {tech.mistake}</div>
              <div style={{ marginTop: 28, padding: "18px 20px", background: "#0F172A", borderRadius: 12, color: "var(--ivory)" }}>
                <div style={{ fontFamily: "var(--mono)", fontSize: 10.5, letterSpacing: "0.2em", color: "var(--gold)", marginBottom: 8 }}>MODERN-LIFE PRACTICE</div>
                <div style={{ fontSize: 14.5, lineHeight: 1.6, color: "rgba(248,241,231,0.85)" }}>
                  {tech.practice}
                </div>
              </div>
            </div>
          </div>

          <div className="tech-extra">
            <div className="tech-extra-cell">
              <div className="lbl">When to use</div>
              <p>{tech.use}</p>
            </div>
            <div className="tech-extra-cell">
              <div className="lbl">When to avoid</div>
              <p>{tech.avoid}</p>
            </div>
            <div className="tech-extra-cell">
              <div className="lbl">Difficulty</div>
              <p style={{ marginBottom: 4 }}>
                {["Beginner-friendly", "Intermediate", "Deep practice"][tech.level - 1]}
              </p>
              <div className="diff-pip">
                {[1, 2, 3].map((n) => <span key={n} className={n <= tech.level ? "on" : ""} />)}
              </div>
            </div>
            {tech.related?.length > 0 && (
              <div className="tech-extra-cell" style={{ gridColumn: "span 2" }}>
                <div className="lbl">Related techniques</div>
                <div className="related-list">
                  {tech.related.map((rid) => {
                    const r = techniquesById[rid];
                    if (!r) return null;
                    return (
                      <Link key={rid} to={`/technique/${r.id}`}>
                        {r.title} <small>Technique {r.id} · {familyMeta[r.family].label}</small>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="tech-extra-cell">
              <div className="lbl">Journal prompt</div>
              <div className="journal-prompt">"{tech.prompt}"</div>
            </div>
          </div>

          <div style={{ marginTop: 50, padding: 32, background: "#fff", border: "1px solid var(--border)", borderRadius: 14, maxWidth: 760, margin: "50px auto 0" }}>
            <div style={{ fontFamily: "var(--mono)", fontSize: 10.5, letterSpacing: "0.2em", color: "var(--copper)", textTransform: "uppercase", marginBottom: 14 }}>
              Your reflection
            </div>
            <h3 style={{ fontFamily: "var(--serif)", fontWeight: 400, fontSize: 26, marginBottom: 8 }}>
              How did it land?
            </h3>
            <p style={{ color: "var(--muted)", fontSize: 14, marginBottom: 18 }}>{tech.prompt}</p>
            <form onSubmit={submitNote}>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={4}
                placeholder="One line is enough. What did you notice?"
                style={{ width: "100%", padding: "12px 14px", border: "1px solid var(--border)", borderRadius: 10, fontFamily: "inherit", fontSize: 15, resize: "vertical" }}
              />
              <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center" }}>
                <button className="btn btn-primary" type="submit" disabled={!note.trim()} style={!note.trim() ? { opacity: 0.5, cursor: "not-allowed" } : {}}>
                  Save Reflection {Icon.arrowSm()}
                </button>
                {saved_ && <span style={{ color: "var(--copper)", fontSize: 13 }}>Saved to your journal.</span>}
                <Link to="/journal" style={{ marginLeft: "auto", fontSize: 13, color: "var(--copper)" }}>See journal →</Link>
              </div>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
